import { Injectable } from '@angular/core';
import { AbstractWsService } from '../../../core/services/AbstractWsService';
import { map, Observable } from 'rxjs';
import { Dictionary, ExportDocumentTypes, Filter, IPagination, Search, SortingTypes } from '@cinetixx/cinetixx-ui';
import { HttpUtils } from '../../../core/utils/HttpUtils';
import { ITableExport } from '../interfaces';
import { TableExportTypes } from '../enums';
import { HttpParams } from '@angular/common/http';
import { Cache } from '../../../core/decorators/cache.decorator';
import { ReloadCache } from '../../../core/decorators/reload-cache.decorator';
import { StoreCacheKeys } from '../../../core/enums';

@Injectable({
  providedIn: 'root'
})
export class ExportsService extends AbstractWsService {

  private readonly _basePath = '/exports';
  private _downloadAfterExportingMap: Record<string, boolean> = {};

  public isDownloadAfterExportingInCurrentOperation(operationId: string): boolean {
    return this._downloadAfterExportingMap[operationId];
  }

  public removeDownloadAfterExportingFromCurrentOperation(operationId: string): void {
    delete this._downloadAfterExportingMap[operationId];
  }

  public getExportFileUrl(exportId: number, print?: boolean): Observable<string> {
    return this.apiService.get(
      `${ this._basePath }/export-file-url`,
      new HttpParams().append('id', exportId).append('print', print)
    );
  }

  public deleteExportsTable(id: number): Observable<void> {
    return this.apiService.delete(`${this._basePath}/table?exportId=${id}`);
  }

  @ReloadCache<void>(StoreCacheKeys.GetTableExports)
  public exportTable(
    properties: Record<string, string>,
    columns: Record<string, string>,
    exportType: TableExportTypes,
    outputTypes: ExportDocumentTypes,
    fileName?: string,
    downloadAfterExporting?: boolean
  ): Observable<void> {
    const operationId = this.prepareGuid();
    this._downloadAfterExportingMap[operationId] = downloadAfterExporting;

    return this.apiService.post(
      `${ this._basePath }/table`,
      {
        properties,
        columns,
        exportType,
        outputTypes,
        operationId,
        fileName,
        downloadAfterExporting
      }
    );
  }

  public getPaginatedTablesExports(
    page: number,
    results: number,
    sortField: string,
    sortOrder: SortingTypes,
    search: Search = [],
    filter: Filter = [],
    dictionary: Dictionary = [],
  ): Observable<IPagination<ITableExport>> {
    return this.apiService.get(
      `${ this._basePath }/tables`,
      HttpUtils.prepareQueryableParams(page, results, sortField, sortOrder, search, filter, dictionary)
    ).pipe(
      map(data => {
        data.results.forEach((x: ITableExport) => {
          x.fileName = x.fileName.split('.')[0];
          return x;
        });

        return data;
      })
    );
  }

  @Cache<IPagination<ITableExport>>(StoreCacheKeys.GetTableExports)
  public getLatestPaginatedTablesExports(
    page: number,
    results: number,
    sortField: string,
    sortOrder: SortingTypes,
    search: Search = [],
    filter: Filter = [],
    dictionary: Dictionary = [],
  ): Observable<IPagination<ITableExport>> {
    return this.apiService.get(
      `${this._basePath}/tables`,
      HttpUtils.prepareQueryableParams(page, results, sortField, sortOrder, search, filter, dictionary)
    ).pipe(
      map(data => {
        data.results = data.results.slice(0, 5).map((x: ITableExport) => {
          x.fileName = x.fileName.split('.')[0];
          return x;
        });

        return data;
      })
    );
  }
}
